/**
 * The form request page's standard look (forms-web.html moment 2 A, centered):
 * Back to home, the kicker, the form's name, its kind and description, the
 * fields through the app's FormRenderer, and Submit; then the app's end
 * screens in the website's type. Draws the behavior half; holds no rule.
 */
import { FormRenderer } from '@/components/forms/FormRenderer';
import { terminalContent } from '@/pages/FormRequestPage/formRequestParts';

import { partsOf, pieceRoot } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import type { FormRequestRuntime } from './useFormRequest';
import css from './FormRequest.module.css';
import { contract } from './contract';

const part = partsOf(contract);

/** A sent form saves through `answersChanged`, never FormRenderer's blur and interval save. */
function noSave() {}

export function FormRequestView({ state, actions, form, serverErrors, answersChanged }: FormRequestRuntime) {
  const { status } = state;
  if (status === 'loading') {
    return (
      <div className={css.loading} role="status" {...pieceRoot(contract)} {...part('page')}>
        Loading form…
      </div>
    );
  }

  if (status !== 'form' && status !== 'submitting') {
    const end = terminalContent(status, form?.title);
    return (
      <section className={css.end} aria-labelledby="formRequestEnd" {...pieceRoot(contract)} {...part('end')}>
        <div className={css.orb} aria-hidden="true">
          {end.orb}
        </div>
        {end.kicker && <div className={css.endKicker}>{end.kicker}</div>}
        <h1 className={css.endTitle} id="formRequestEnd" {...part('endTitle')}>
          {end.heading}
        </h1>
        <p className={css.endText} {...part('endText')}>
          {end.body}
        </p>
        <div className={css.endActions}>
          {status === 'error' && (
            <button type="button" className={`${shared.btn} ${shared.btnOutline}`} onClick={actions.retry} {...part('retry')}>
              Try again
            </button>
          )}
          <button type="button" className={`${shared.btn} ${shared.btnPrimary}`} onClick={actions.goHome} {...part('home')}>
            Back to Home
            <Icon name="arrow" />
          </button>
        </div>
      </section>
    );
  }

  if (!form) return null;
  return (
    <div className={css.page} {...pieceRoot(contract)} {...part('page')}>
      <button type="button" className={css.back} onClick={actions.goHome} {...part('back')}>
        <Icon name="back" small />
        Back to home
      </button>
      <div className={css.kicker} {...part('kicker')}>
        Form request
      </div>
      <h1 className={css.name} {...part('title')}>
        {form.title}
      </h1>
      <div className={css.intro}>
        <div className={css.kind} {...part('kind')}>
          {state.kind}
        </div>
        {form.description && (
          <p className={css.desc} {...part('description')}>
            {form.description}
          </p>
        )}
        {state.anonymous && (
          <div className={css.anon} {...part('anonymous')}>
            <Icon name="lock" small />
            Anonymous · your answers aren&rsquo;t linked to your name
          </div>
        )}
      </div>
      <div className={css.fields}>
        <FormRenderer
          form={form}
          fieldsAttributes={part('fields')}
          onSubmit={actions.submit}
          onSave={noSave}
          onAnswersChange={answersChanged}
          isSubmitting={status === 'submitting'}
          serverErrors={serverErrors}
          disableAutoSave
          appearance="clientBooking"
          renderActions={({ isSubmitting }) => (
            <div className={css.bar}>
              {state.submitFailed && !isSubmitting && (
                <p className={css.submitError} role="alert" {...part('submitError')}>Couldn't send your answers. Please try again.</p>
              )}
              <button
                type="submit"
                className={`${shared.btn} ${shared.btnPrimary} ${css.submit}`}
                disabled={isSubmitting}
                {...part('submit')}
              >
                {isSubmitting ? 'Submitting…' : 'Submit'}
                {!isSubmitting && <Icon name="arrow" />}
              </button>
            </div>
          )}
        />
      </div>
    </div>
  );
}
